/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {isBlank, isPresent} from '../facade/lang';
import {ParseError, ParseSourceSpan} from '../parse_util';

import * as html from './ast';
import {DEFAULT_INTERPOLATION_CONFIG, InterpolationConfig} from './interpolation_config';
import * as lex from './lexer';
import {TagDefinition, getNsPrefix, mergeNsAndName} from './tags';

export class TreeError extends ParseError {
/**
 * @param {?} elementName
 * @param {?} span
 * @param {?} msg
 * @return {?}
 */
static create(elementName: string, span: ParseSourceSpan, msg: string): TreeError {
    return new TreeError(elementName, span, msg);
  }
/**
 * @param {?} elementName
 * @param {?} span
 * @param {?} msg
 */
constructor(public elementName: string, span: ParseSourceSpan, msg: string) { super(span, msg); }
}

function TreeError_tsickle_Closure_declarations() {
 /** @type {?} */
TreeError.prototype.elementName;
}


export class ParseTreeResult {
/**
 * @param {?} rootNodes
 * @param {?} errors
 */
constructor(public rootNodes: html.Node[],
public errors: ParseError[]) {}
}

function ParseTreeResult_tsickle_Closure_declarations() {
 /** @type {?} */
ParseTreeResult.prototype.rootNodes;
 /** @type {?} */
ParseTreeResult.prototype.errors;
}


export class Parser {
/**
 * @param {?} getTagDefinition
 */
constructor(public getTagDefinition: (tagName: string) => TagDefinition) {}
/**
 * @param {?} source
 * @param {?} url
 * @param {?=} parseExpansionForms
 * @param {?=} interpolationConfig
 * @return {?}
 */
parse(
      source: string, url: string, parseExpansionForms: boolean = false,
      interpolationConfig: InterpolationConfig = DEFAULT_INTERPOLATION_CONFIG): ParseTreeResult {
    const /** @type {?} */ tokensAndErrors =
        lex.tokenize(source, url, this.getTagDefinition, parseExpansionForms, interpolationConfig);

    const /** @type {?} */ treeAndErrors = new _TreeBuilder(tokensAndErrors.tokens, this.getTagDefinition).build();

    return new ParseTreeResult(
        treeAndErrors.rootNodes,
        ( /** @type {?} */((<ParseError[]>tokensAndErrors.errors))).concat(treeAndErrors.errors));
  }
}

function Parser_tsickle_Closure_declarations() {
 /** @type {?} */
Parser.prototype.getTagDefinition;
}


class _TreeBuilder {
private _index: number = -1;
private _peek: lex.Token;
private _rootNodes: html.Node[] = [];
private _errors: TreeError[] = [];
private _elementStack: html.Element[] = [];
/**
 * @param {?} tokens
 * @param {?} getTagDefinition
 */
constructor(
private tokens: lex.Token[],
private getTagDefinition: (tagName: string) => TagDefinition) {
    this._advance();
  }
/**
 * @return {?}
 */
build(): ParseTreeResult {
    while (this._peek.type !== lex.TokenType.EOF) {
      if (this._peek.type === lex.TokenType.TAG_OPEN_START) {
        this._consumeStartTag(this._advance());
      } else if (this._peek.type === lex.TokenType.TAG_CLOSE) {
        this._consumeEndTag(this._advance());
      } else if (this._peek.type === lex.TokenType.CDATA_START) {
        this._closeVoidElement();
        this._consumeCdata(this._advance());
      } else if (this._peek.type === lex.TokenType.COMMENT_START) {
        this._closeVoidElement();
        this._consumeComment(this._advance());
      } else if (
          this._peek.type === lex.TokenType.TEXT || this._peek.type === lex.TokenType.RAW_TEXT ||
          this._peek.type === lex.TokenType.ESCAPABLE_RAW_TEXT) {
        this._closeVoidElement();
        this._consumeText(this._advance());
      } else if (this._peek.type === lex.TokenType.EXPANSION_FORM_START) {
        this._consumeExpansion(this._advance());
      } else {
        // Skip all other tokens...
        this._advance();
      }
    }
    return new ParseTreeResult(this._rootNodes, this._errors);
  }
/**
 * @return {?}
 */
private _advance(): lex.Token {
    const /** @type {?} */ prev = this._peek;
    if (this._index < this.tokens.length - 1) {
      // Note: there is always an EOF token at the end
      this._index++;
    }
    this._peek = this.tokens[this._index];
    return prev;
  }
/**
 * @param {?} type
 * @return {?}
 */
private _advanceIf(type: lex.TokenType): lex.Token {
    if (this._peek.type === type) {
      return this._advance();
    }
    return null;
  }
/**
 * @param {?} startToken
 * @return {?}
 */
private _consumeCdata(startToken: lex.Token) {
    this._consumeText(this._advance());
    this._advanceIf(lex.TokenType.CDATA_END);
  }
/**
 * @param {?} token
 * @return {?}
 */
private _consumeComment(token: lex.Token) {
    const /** @type {?} */ text = this._advanceIf(lex.TokenType.RAW_TEXT);
    this._advanceIf(lex.TokenType.COMMENT_END);
    const /** @type {?} */ value = isPresent(text) ? text.parts[0].trim() : null;
    this._addToParent(new html.Comment(value, token.sourceSpan));
  }
/**
 * @param {?} token
 * @return {?}
 */
private _consumeExpansion(token: lex.Token) {
    const /** @type {?} */ switchValue = this._advance();

    const /** @type {?} */ type = this._advance();
    const /** @type {?} */ cases: html.ExpansionCase[] = [];

    // read =
    while (this._peek.type === lex.TokenType.EXPANSION_CASE_VALUE) {
      const /** @type {?} */ expCase = this._parseExpansionCase();
      if (!expCase) return;  // error
      cases.push(expCase);
    }

    // read the final }
    if (this._peek.type !== lex.TokenType.EXPANSION_FORM_END) {
      this._errors.push(
          TreeError.create(null, this._peek.sourceSpan, `Invalid ICU message. Missing '}'.`));
      return;
    }
    const /** @type {?} */ sourceSpan = new ParseSourceSpan(token.sourceSpan.start, this._peek.sourceSpan.end);
    this._addToParent(new html.Expansion(
        switchValue.parts[0], type.parts[0], cases, sourceSpan, switchValue.sourceSpan));

    this._advance();
  }
/**
 * @return {?}
 */
private _parseExpansionCase(): html.ExpansionCase {
    const /** @type {?} */ value = this._advance();

    // read {
    if (this._peek.type !== lex.TokenType.EXPANSION_CASE_EXP_START) {
      this._errors.push(
          TreeError.create(null, this._peek.sourceSpan, `Invalid ICU message. Missing '{'.`));
      return null;
    }

    // read until }
    const /** @type {?} */ start = this._advance();

    const /** @type {?} */ exp = this._collectExpansionExpTokens(start);
    if (!exp) return null;

    const /** @type {?} */ end = this._advance();
    exp.push(new lex.Token(lex.TokenType.EOF, [], end.sourceSpan));

    // parse everything in between { and }
    const /** @type {?} */ parsedExp = new _TreeBuilder(exp, this.getTagDefinition).build();
    if (parsedExp.errors.length > 0) {
      this._errors = this._errors.concat( /** @type {?} */((<TreeError[]>parsedExp.errors)));
      return null;
    }

    const /** @type {?} */ sourceSpan = new ParseSourceSpan(value.sourceSpan.start, end.sourceSpan.end);
    const /** @type {?} */ expSourceSpan = new ParseSourceSpan(start.sourceSpan.start, end.sourceSpan.end);
    return new html.ExpansionCase(
        value.parts[0], parsedExp.rootNodes, sourceSpan, value.sourceSpan, expSourceSpan);
  }
/**
 * @param {?} start
 * @return {?}
 */
private _collectExpansionExpTokens(start: lex.Token): lex.Token[] {
    const /** @type {?} */ exp: lex.Token[] = [];
    const /** @type {?} */ expansionFormStack = [lex.TokenType.EXPANSION_CASE_EXP_START];

    while (true) {
      if (this._peek.type === lex.TokenType.EXPANSION_FORM_START ||
          this._peek.type === lex.TokenType.EXPANSION_CASE_EXP_START) {
        expansionFormStack.push(this._peek.type);
      }

      if (this._peek.type === lex.TokenType.EXPANSION_CASE_EXP_END) {
        if (lastOnStack(expansionFormStack, lex.TokenType.EXPANSION_CASE_EXP_START)) {
          expansionFormStack.pop();
          if (expansionFormStack.length == 0) return exp;

        } else {
          this._errors.push(
              TreeError.create(null, start.sourceSpan, `Invalid ICU message. Missing '}'.`));
          return null;
        }
      }

      if (this._peek.type === lex.TokenType.EXPANSION_FORM_END) {
        if (lastOnStack(expansionFormStack, lex.TokenType.EXPANSION_FORM_START)) {
          expansionFormStack.pop();
        } else {
          this._errors.push(
              TreeError.create(null, start.sourceSpan, `Invalid ICU message. Missing '}'.`));
          return null;
        }
      }

      if (this._peek.type === lex.TokenType.EOF) {
        this._errors.push(
            TreeError.create(null, start.sourceSpan, `Invalid ICU message. Missing '}'.`));
        return null;
      }

      exp.push(this._advance());
    }
  }
/**
 * @param {?} token
 * @return {?}
 */
private _consumeText(token: lex.Token) {
    let /** @type {?} */ text = token.parts[0];
    if (text.length > 0 && text[0] == '\n') {
      const /** @type {?} */ parent = this._getParentElement();
      if (isPresent(parent) && parent.children.length == 0 &&
          this.getTagDefinition(parent.name).ignoreFirstLf) {
        text = text.substring(1);
      }
    }

    if (text.length > 0) {
      this._addToParent(new html.Text(text, token.sourceSpan));
    }
  }
/**
 * @return {?}
 */
private _closeVoidElement(): void {
    if (this._elementStack.length > 0) {
      const /** @type {?} */ el = this._elementStack[this._elementStack.length - 1];

      if (this.getTagDefinition(el.name).isVoid) {
        this._elementStack.pop();
      }
    }
  }
/**
 * @param {?} startTagToken
 * @return {?}
 */
private _consumeStartTag(startTagToken: lex.Token) {
    const /** @type {?} */ prefix = startTagToken.parts[0];
    const /** @type {?} */ name = startTagToken.parts[1];
    const /** @type {?} */ attrs: html.Attribute[] = [];
    while (this._peek.type === lex.TokenType.ATTR_NAME) {
      attrs.push(this._consumeAttr(this._advance()));
    }
    const /** @type {?} */ fullName = this._getElementFullName(prefix, name, this._getParentElement());
    let /** @type {?} */ selfClosing = false;
    // Note: There could have been a tokenizer error
    // so that we don't get a token for the end tag...
    if (this._peek.type === lex.TokenType.TAG_OPEN_END_VOID) {
      this._advance();
      selfClosing = true;
      const /** @type {?} */ tagDef = this.getTagDefinition(fullName);
      if (!(tagDef.canSelfClose || getNsPrefix(fullName) !== null || tagDef.isVoid)) {
        this._errors.push(TreeError.create(
            fullName, startTagToken.sourceSpan,
            `Only void and foreign elements can be self closed "${startTagToken.parts[1]}"`));
      }
    } else if (this._peek.type === lex.TokenType.TAG_OPEN_END) {
      this._advance();
      selfClosing = false;
    }
    const /** @type {?} */ end = this._peek.sourceSpan.start;
    const /** @type {?} */ span = new ParseSourceSpan(startTagToken.sourceSpan.start, end);
    const /** @type {?} */ el = new html.Element(fullName, attrs, [], span, span, null);
    this._pushElement(el);
    if (selfClosing) {
      this._popElement(fullName);
      el.endSourceSpan = span;
    }
  }
/**
 * @param {?} el
 * @return {?}
 */
private _pushElement(el: html.Element) {
    if (this._elementStack.length > 0) {
      const /** @type {?} */ parentEl = this._elementStack[this._elementStack.length - 1];
      if (this.getTagDefinition(parentEl.name).isClosedByChild(el.name)) {
        this._elementStack.pop();
      }
    }

    const /** @type {?} */ tagDef = this.getTagDefinition(el.name);
    const {parent, container} = this._getParentElementSkippingContainers();

    if (isPresent(parent) && tagDef.requireExtraParent(parent.name)) {
      const /** @type {?} */ newParent = new html.Element(
          tagDef.parentToAdd, [], [], el.sourceSpan, el.startSourceSpan, el.endSourceSpan);
      this._insertBeforeContainer(parent, container, newParent);
    }

    this._addToParent(el);
    this._elementStack.push(el);
  }
/**
 * @param {?} endTagToken
 * @return {?}
 */
private _consumeEndTag(endTagToken: lex.Token) {
    const /** @type {?} */ fullName = this._getElementFullName(
        endTagToken.parts[0], endTagToken.parts[1], this._getParentElement());

    if (this._getParentElement()) {
      this._getParentElement().endSourceSpan = endTagToken.sourceSpan;
    }

    if (this.getTagDefinition(fullName).isVoid) {
      this._errors.push(TreeError.create(
          fullName, endTagToken.sourceSpan,
          `Void elements do not have end tags "${endTagToken.parts[1]}"`));
    } else if (!this._popElement(fullName)) {
      this._errors.push(TreeError.create(
          fullName, endTagToken.sourceSpan, `Unexpected closing tag "${endTagToken.parts[1]}"`));
    }
  }
/**
 * @param {?} fullName
 * @return {?}
 */
private _popElement(fullName: string): boolean {
    for (let /** @type {?} */ stackIndex = this._elementStack.length - 1; stackIndex >= 0; stackIndex--) {
      const /** @type {?} */ el = this._elementStack[stackIndex];
      if (el.name == fullName) {
        this._elementStack.splice(stackIndex, this._elementStack.length - stackIndex);
        return true;
      }

      if (!this.getTagDefinition(el.name).closedByParent) {
        return false;
      }
    }
    return false;
  }
/**
 * @param {?} attrName
 * @return {?}
 */
private _consumeAttr(attrName: lex.Token): html.Attribute {
    const /** @type {?} */ fullName = mergeNsAndName(attrName.parts[0], attrName.parts[1]);
    let /** @type {?} */ end = attrName.sourceSpan.end;
    let /** @type {?} */ value = '';
    let /** @type {?} */ valueSpan: ParseSourceSpan;
    if (this._peek.type === lex.TokenType.ATTR_VALUE) {
      const /** @type {?} */ valueToken = this._advance();
      value = valueToken.parts[0];
      end = valueToken.sourceSpan.end;
      valueSpan = valueToken.sourceSpan;
    }
    return new html.Attribute(
        fullName, value, new ParseSourceSpan(attrName.sourceSpan.start, end), valueSpan);
  }
/**
 * @return {?}
 */
private _getParentElement(): html.Element {
    return this._elementStack.length > 0 ? this._elementStack[this._elementStack.length - 1] : null;
  }
/**
 *  Returns the parent in the DOM and the container.
  * *
  * `<ng-container>` elements are skipped as they are not rendered as DOM element.
 * @return {?}
 */
private _getParentElementSkippingContainers(): {parent: html.Element, container: html.Element} {
    let /** @type {?} */ container: html.Element = null;

    for (let /** @type {?} */ i = this._elementStack.length - 1; i >= 0; i--) {
      if (this._elementStack[i].name !== 'ng-container') {
        return {parent: this._elementStack[i], container};
      }
      container = this._elementStack[i];
    }

    return {parent: this._elementStack[this._elementStack.length - 1], container};
  }
/**
 * @param {?} node
 * @return {?}
 */
private _addToParent(node: html.Node) {
    const /** @type {?} */ parent = this._getParentElement();
    if (isPresent(parent)) {
      parent.children.push(node);
    } else {
      this._rootNodes.push(node);
    }
  }
/**
 *  Insert a node between the parent and the container.
  * When no container is given, the node is appended as a child of the parent.
  * Also updates the element stack accordingly.
  * *
 * @param {?} parent
 * @param {?} container
 * @param {?} node
 * @return {?}
 */
private _insertBeforeContainer(
      parent: html.Element, container: html.Element, node: html.Element) {
    if (!container) {
      this._addToParent(node);
      this._elementStack.push(node);
    } else {
      if (parent) {
        // replace the container with the new node in the children
        const /** @type {?} */ index = parent.children.indexOf(container);
        parent.children[index] = node;
      } else {
        this._rootNodes.push(node);
      }
      node.children.push(container);
      this._elementStack.splice(this._elementStack.indexOf(container), 0, node);
    }
  }
/**
 * @param {?} prefix
 * @param {?} localName
 * @param {?} parentElement
 * @return {?}
 */
private _getElementFullName(prefix: string, localName: string, parentElement: html.Element):
      string {
    if (isBlank(prefix)) {
      prefix = this.getTagDefinition(localName).implicitNamespacePrefix;
      if (isBlank(prefix) && isPresent(parentElement)) {
        prefix = getNsPrefix(parentElement.name);
      }
    }

    return mergeNsAndName(prefix, localName);
  }
}

function _TreeBuilder_tsickle_Closure_declarations() {
 /** @type {?} */
_TreeBuilder.prototype._index;
 /** @type {?} */
_TreeBuilder.prototype._peek;
 /** @type {?} */
_TreeBuilder.prototype._rootNodes;
 /** @type {?} */
_TreeBuilder.prototype._errors;
 /** @type {?} */
_TreeBuilder.prototype._elementStack;
 /** @type {?} */
_TreeBuilder.prototype.tokens;
 /** @type {?} */
_TreeBuilder.prototype.getTagDefinition;
}

/**
 * @param {?} stack
 * @param {?} element
 * @return {?}
 */
function lastOnStack(stack: any[], element: any): boolean {
  return stack.length > 0 && stack[stack.length - 1] === element;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
